import { useState, useEffect } from "react";
import { Users, Folder, Settings, BarChart, Phone, LogOut, Globe, Edit, Navigation, Sparkles, Home, Image, ChevronRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import {
  Sidebar,
  SidebarContent,
} from "@/components/ui/sidebar";

import "../admin-sidebar-mobile.css";

interface UserProfile {
  id: string;
  email: string;
  username?: string;
  avatar_url?: string;
}

interface NavItem {
  title: string;
  icon: React.ElementType;
  href: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const sections: NavSection[] = [
  {
    label: "Website",
    items: [
      { title: "Dashboard", icon: Home, href: "/admin" },
      { title: "Pagina's", icon: Edit, href: "/admin/cms" },
      { title: "Expertises", icon: Sparkles, href: "/admin/cms/expertises" },
      { title: "Navigatie", icon: Navigation, href: "/admin/cms/navigation" },
      { title: "Media", icon: Image, href: "/admin/cms/media" },
    ],
  },
  {
    label: "Instellingen",
    items: [
      { title: "Algemeen", icon: Settings, href: "/admin/settings" },
      { title: "Site config", icon: Globe, href: "/admin/cms/settings" },
      { title: "Team", icon: Users, href: "/admin/team" },
    ],
  },
  {
    label: "Analyses",
    items: [
      { title: "Statistieken", icon: BarChart, href: "#" },
      { title: "Contactverzoeken", icon: Phone, href: "#" },
    ],
  },
];

const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [user, setUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, username, avatar_url')
          .eq('id', session.user.id)
          .single();
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          username: profile?.username,
          avatar_url: profile?.avatar_url,
        });
      } catch (error) {
        console.error("Fout bij ophalen profiel:", error);
      }
    };
    loadUserProfile();
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('supabase.')) localStorage.removeItem(key);
      });
      toast({ title: "Uitgelogd", description: "Je bent succesvol uitgelogd." });
      navigate('/login');
    } catch {
      localStorage.clear();
      navigate('/login');
    }
  };

  const isActive = (href: string) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  const initials = user?.username
    ? user.username.split(' ').map(w => w[0]?.toUpperCase()).join('').slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || 'V';

  return (
    <Sidebar className="bg-[#1a1a1a] border-r border-white/[0.06] admin-sidebar-custom">
      <SidebarContent className="flex flex-col h-full p-0">
        {/* Logo */}
        <a href="/" className="flex items-center h-14 px-4 border-b border-white/[0.06] shrink-0">
          <img
            src="/lovable-uploads/f0397fdb-65b1-425f-a824-0229bd3e12d4.png"
            alt="Vermado"
            className="h-6 w-auto"
          />
        </a>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {sections.map((section) => (
            <div key={section.label} className="mb-4">
              <p className="px-3 mb-1 text-[11px] font-medium uppercase tracking-wider text-white/40">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <li key={item.href}>
                      <a
                        href={item.href}
                        className={`flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] transition-colors ${
                          active
                            ? 'bg-white/[0.08] text-white font-medium'
                            : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                        }`}
                      >
                        <item.icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-white' : 'text-white/40'}`} />
                        <span>{item.title}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/[0.06] p-2 shrink-0">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-white/50 hover:text-white hover:bg-white/[0.04] transition-colors text-[13px]"
          >
            <LogOut className="h-4 w-4" />
            <span>Uitloggen</span>
          </button>
          <div className="flex items-center gap-2.5 px-3 py-2 mt-1">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
            ) : (
              <div className="h-7 w-7 rounded-full bg-[#B1A06D]/20 flex items-center justify-center">
                <span className="text-[11px] font-semibold text-[#B1A06D]">{initials}</span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-white truncate">{user?.username || 'Admin'}</p>
              <p className="text-[11px] text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};

export default AdminSidebar;
